import { config } from '@abyss/web/tools/config';
import { Flex } from '@abyss/web/ui/Flex';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { ReverseCoverageTypesCodes } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { DataCardContainer } from '../../../../common/DataCard/DataCardContainer';
import { OutsideCountyAlert } from '../../../../common/DataCard/OutsideCountyAlert';
import { PortalIdentifier } from '../../../../common/Portal';
import { getGeoLocationFromStorage } from '../../../../common/PSXHeader/SearchBar/utils';
import {
  DataCardStyle,
  PopUpDataCardStyle,
} from '../../../../common/ResultSection/ResultSection.style';
import { SelectLocationBadge } from '../../../../common/SelectLocationCard/SelectLocationCard.styled';
import { isOutOfCounty } from '../../../../common/SnackCardContainer/utils';
import { CountySearchContext } from '../../../../context/CountySearchContext';
import { PortalContext } from '../../../../context/PortalContext';
import { useCoverageType } from '../../../../hooks/useCoverageType';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import { FacilityLocationsResponse } from '../../../../models/FacilityDetails';
import { Provider } from '../../../../models/Provider';
import { ProviderLocation } from '../../../../models/ProviderDetails';
import { ResponseHeaders } from '../../../../models/ResponseHeaders';
import { shouldUseGehaV2Styling } from '../../../../utils/gehaThemeUtils';
import {
  closePopUp,
  maxPopupHeight,
  maxPopupWidth,
} from '../../../../utils/map.utils';
import { getFormattedAddress } from '../../../../utils/providerDetails.utils';
import { PopupStyle } from './MapView.styled';
import {
  ChooseLocationAddress,
  ChooseLocationButton,
  ChooseLocationMainContainer,
  ChooseLocationPopupContainer,
  ChooseLocationProvider,
  ChooseLocationTile,
} from './Popup.styled';

const INFO_POPUP_MAX_WIDTH = 643;
type Props = {
  headers?: ResponseHeaders;
  practitioner?: Provider;
  locationForAnalytics?: string;
  pageNumber?: number;
  selectedFilters?: string;
  handleClickLocation?: (locationId, locationLng, locationLat) => void;
  updatePin?(
    content: ProviderLocation | FacilityLocationsResponse | null
  ): void;
  isMNR?: boolean;
  isChooseLocation?: boolean;
  selectLocation(): void;
  selected?: boolean;
  index?: number;
  totalLocations?: number;
  recommendationPreferences?: any;
  searchRadius?: any;
};
export const Popup = ({
  headers,
  practitioner,
  locationForAnalytics,
  pageNumber,
  selectedFilters,
  handleClickLocation,
  updatePin,
  isMNR,
  isChooseLocation = false,
  selectLocation,
  selected = false,
  index = 0,
  totalLocations = 0,
  recommendationPreferences,
  searchRadius,
}: Props) => {
  const [mapviewEnhancements, includeProviderGroupName] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
    ConstantsLagoon.FEATURE_FLAGS.INCLUDE_PROVIDER_GROUP_NAME,
  ]);

  const [popupContainer, setPopupContainer] = useState<any>(null);

  const coverageType = useCoverageType();
  const lagoonData = useLagoon('ui-messaging')();
  const nationaAncillaryNoLocation = find(lagoonData, {
    key: ConstantsLagoon.NATIONAL_ANCILLARY_NO_LOCATION_MODAL,
  });
  const visionOnlineProvidersNoLocation = find(lagoonData, {
    key: ConstantsLagoon.ONLINE_PROVIDERS_VISION_NO_LOCATION_MODAL,
  });

  const [countySearchAlertFlag] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_COUNTY_SEARCH_ALERT,
  ]);
  const { isCountySearch } = useContext(CountySearchContext);
  const { name, stateCode } = getGeoLocationFromStorage();

  const { t } = useTranslation();

  const enterIntoMapPopUp = (e, rootElem) => {
    if (
      e.key === 'Tab' &&
      (rootElem as HTMLElement)?.style.visibility === 'visible'
    ) {
      (rootElem as HTMLElement).focus();
    }
  };

  const exitMapPopUp = (e, practitioner, rootElem) => {
    if (e.key === 'Escape') {
      const currentPinPoint = document.querySelector(
        `#custom-marker-${practitioner.providerId}`
      );
      const tooltip = document.querySelector('.mapboxgl-popup-tip');

      closePopUp(currentPinPoint, rootElem, tooltip, updatePin);
    }
  };

  useEffect(() => {
    let rootElem: Element | null;
    let mapElem: Element | null;

    const handleMapElemFocus = () => {
      (mapElem as HTMLElement).addEventListener(
        'keydown',
        handleMapElemKeydown
      );
    };

    const handleMapElemKeydown = (e: any) => {
      enterIntoMapPopUp(e, rootElem);
    };

    const handleRootElemKeydown = (e: any) => {
      if (practitioner && rootElem) {
        exitMapPopUp(e, practitioner, rootElem);
      }
    };

    if (practitioner) {
      rootElem = document.querySelector('.datacard-popup');
      if (rootElem) {
        (rootElem as HTMLElement).tabIndex = 0;
        (rootElem as HTMLElement).style.width = mapviewEnhancements
          ? '151px'
          : 'fit-content';
        if (!practitioner?.address?.line?.[0] && !isChooseLocation) {
          (rootElem as HTMLElement).style.width = `${maxPopupWidth(
            undefined,
            INFO_POPUP_MAX_WIDTH
          )}px`;
          (rootElem as HTMLElement).style.height = `${maxPopupHeight(
            undefined,
            286
          )}px`;
        }

        (rootElem as HTMLElement).style.visibility = 'visible';
        mapElem = document.querySelector('.mapboxgl-canvas');

        mapElem?.addEventListener('focus', handleMapElemFocus);

        (rootElem as HTMLElement).addEventListener(
          'keydown',
          handleRootElemKeydown
        );
      }
      setPopupContainer(rootElem);
      document.querySelectorAll('.mapboxgl-popup-tip').forEach((el) => {
        (el as HTMLElement).style.display = mapviewEnhancements
          ? 'none'
          : 'block';
        if (!practitioner?.address?.line?.[0]) {
          (el as HTMLElement).style.visibility = 'hidden';
        }
      });
    }

    return () => {
      if (rootElem && mapElem) {
        mapElem.removeEventListener('keydown', handleMapElemKeydown);
        mapElem.removeEventListener('focus', handleMapElemFocus);
        rootElem.removeEventListener('keydown', handleRootElemKeydown);
      }
    };
  }, [practitioner]);

  const { portalData } = useContext(PortalContext);
  const isGeha = portalData?.portalIdentifier === PortalIdentifier.GEHA;
  const isGEHAV2 = shouldUseGehaV2Styling();
  const isGehaTheme = isGEHAV2 || isGeha;

  const handleDirectionClick = (provider) => {
    if (handleClickLocation)
      handleClickLocation(
        provider.locationId,
        provider.longitude,
        provider.latitude
      );
  };

  const displayOutOfCntyIndicator = (provider) => {
    const isOutOfCnty = isOutOfCounty(
      isCountySearch,
      name,
      stateCode,
      provider
    );
    return (
      countySearchAlertFlag &&
      isOutOfCnty && (
        <OutsideCountyAlert
          county={name?.split(',')[0]}
          isOutOfCounty={isOutOfCnty}
        />
      )
    );
  };

  const showPopup = () =>
    practitioner?.address?.line?.[0] ? (
      <>
        <DataCardContainer
          badgeCardType="snack-card-map-popup"
          css={mapviewEnhancements ? PopUpDataCardStyle : DataCardStyle}
          handleDirectionClick={() => handleDirectionClick(practitioner)}
          headers={headers}
          isMNR={isMNR}
          isPopup={true}
          locationForAnalytics={locationForAnalytics}
          pageNumber={pageNumber}
          popupContainer={popupContainer && mapviewEnhancements}
          practitioner={practitioner || {}}
          recommendationPreferences={recommendationPreferences}
          searchRadius={searchRadius}
          selectedFilters={selectedFilters}
        />
        {displayOutOfCntyIndicator(practitioner)}
      </>
    ) : (
      <Flex
        className="non-provider-popup"
        css={{
          display: 'flex',
          width: `${INFO_POPUP_MAX_WIDTH}px`,
          padding: '32px 24px',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px',
          borderRadius: '10.08px',
          background: 'rgba(255, 255, 255, 0.80)',
          '@screen < $md': {
            width: '311px',
            padding: '40px 32px',
            borderRadius: '12px',
          },
        }}
      >
        {!isGehaTheme && (
          <img
            alt="national ancillary facility"
            src={`${config(
              'CDN_BASE_URL'
            )}/cdn/assets/images/national_ancillary_facility.png`}
          />
        )}

        <Text
          css={{
            'abyss-text-root': {
              textAlign: 'center',
              alignSelf: 'stretch',
              fontSize: '18px',
              fontWeight: '$bold',
              lineHeight: '24px',
              color: '#000',
            },
          }}
          data-auto-testid="no-location-text"
          data-testid="no-location-text"
        >
          {coverageType === ReverseCoverageTypesCodes.VISION
            ? t('noLocationInfoVision')
            : t('Find providers near you')}
        </Text>
        <Text
          css={{
            'abyss-text-root': {
              textAlign: 'center',
              alignSelf: 'stretch',
              fontSize: '18px',
              fontWeight: '$medium',
              lineHeight: '24px',
              color: '#000',
            },
          }}
        >
          {coverageType === ReverseCoverageTypesCodes.VISION
            ? visionOnlineProvidersNoLocation?.message
            : nationaAncillaryNoLocation?.message}
        </Text>
      </Flex>
    );

  const chooseLocationPopup = () =>
    (practitioner?.providerName || practitioner?.address) && (
      <ChooseLocationPopupContainer className="choose-location-popup">
        <ChooseLocationMainContainer>
          <div>
            <Text
              color="$gray8"
              fontSize="12px"
              fontWeight="600"
              lineHeights="16px"
              size="sm"
            >
              {t('CHOOSE_PCP_FLOW.LOCATION_NUMBER', {
                index: index + 1,
                total: totalLocations,
              })}
            </Text>
          </div>
          <ChooseLocationTile
            data-auto-testid="choose-location-title"
            data-testid="choose-location-title"
          >
            <ChooseLocationProvider
              data-auto-testid="choose-location-text"
              data-testid="choose-location-text"
            >
              {practitioner?.providerName}
            </ChooseLocationProvider>
            <ChooseLocationAddress>
              {getFormattedAddress(
                practitioner?.address,
                includeProviderGroupName
              )}
            </ChooseLocationAddress>
          </ChooseLocationTile>
          {selected ? (
            <SelectLocationBadge
              icon={
                <IconSymbol
                  color="$success1"
                  icon="check_circle"
                  size="$sm"
                  variant="filled"
                />
              }
              variant="success"
            >
              {t('CHOOSE_PCP_FLOW.SELECTED_LOCATION')}
            </SelectLocationBadge>
          ) : (
            <ChooseLocationButton
              data-testid="choose-location-button"
              onClick={selectLocation}
              variant="tertiary"
            >
              {t('CHOOSE_PCP_FLOW.CHOOSE_THIS_LOCATION')}
            </ChooseLocationButton>
          )}
        </ChooseLocationMainContainer>
      </ChooseLocationPopupContainer>
    );

  return (
    <React.Fragment>
      {popupContainer !== null &&
        createPortal(
          <PopupStyle>
            {isChooseLocation ? chooseLocationPopup() : showPopup()}
          </PopupStyle>,
          popupContainer
        )}
    </React.Fragment>
  );
};
